/**
 * AddressService
 * Service for Address entity operations
 * Route: api/addresses
 * Controller: AddressesController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * AddressService
 * Handles address CRUD operations
 * Maps to /api/addresses endpoint
 * Note: Address uses LocationID as primary key
 */
export class AddressService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? AddressService.DEFAULT_SERVICE_PATH,
            basePath: AddressService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get address by location ID
     * GET /api/addresses/{locationId}
     * @param locationId - Location ID (also serves as Address PK)
     * @returns Address for the location
     */
    async getByLocationId(locationId) {
        const response = await this.getByLocationIdRaw(locationId);
        if (!response.data) {
            throw new Error(`Address for location ${locationId} not found`);
        }
        return response.data;
    }
    /**
     * Get address by location ID (raw)
     * @param locationId - Location ID
     * @returns ResponseData with address
     */
    getByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('byLocationId', `${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Override getById to use locationId
     * @param id - Location ID
     * @returns Address for the location
     */
    async getById(id) {
        return this.getByLocationId(id);
    }
    /**
     * Override getByIdRaw to use locationId
     * @param id - Location ID
     * @returns ResponseData with address
     */
    getByIdRaw(id) {
        return this.getByLocationIdRaw(id);
    }
    /**
     * Update address by location ID
     * PUT /api/addresses/{locationId}
     * @param locationId - Location ID
     * @param address - Address data to update
     * @returns Updated address
     */
    async updateByLocationId(locationId, address) {
        const response = await this.updateByLocationIdRaw(locationId, address);
        if (!response.data) {
            throw new Error(`Failed to update address for location ${locationId}`);
        }
        return response.data;
    }
    /**
     * Update address by location ID (raw)
     * @param locationId - Location ID
     * @param address - Address data to update
     * @returns ResponseData with updated address
     */
    updateByLocationIdRaw(locationId, address) {
        const endpoint = this.getEndpoint('update', `${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, address);
    }
    /**
     * Delete address by location ID
     * DELETE /api/addresses/{locationId}
     * @param locationId - Location ID
     * @returns True if deleted successfully
     */
    async deleteByLocationId(locationId) {
        const response = await this.deleteByLocationIdRaw(locationId);
        return response.success;
    }
    /**
     * Delete address by location ID (raw)
     * @param locationId - Location ID
     * @returns ResponseData
     */
    deleteByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('delete', `${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
}
AddressService.DEFAULT_SERVICE_PATH = 'locations';
AddressService.BASE_PATH = 'api/addresses';
//# sourceMappingURL=AddressService.js.map